import { action, computed, observable, reaction } from "mobx";
import type RootStore from "./RootStore";
import { BoostOption } from "@/services/types";
import { gameStore } from "@/store";
import { backend } from "@/services";

interface State {
  attempts: number;
  lastUseTimestamp: number | null;
}

interface Options {
  id: number;
  name: string;
  description: string;
  attempts: number;
  duration: number;
}

export default class BoostEnergyInstantStore {
  rootStore: RootStore;
  @observable.ref private accessor options: Options | undefined;
  @observable private accessor state: State | null = null;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @action init(bo: BoostOption | undefined) {
    if (!bo) {
      throw new Error("Empty options for Instant boost");
    }
    if (bo.attempts) {
      this.options = {
        id: bo.externalId,
        name: bo.nameEn,
        description: bo.descriptionEn,
        attempts: bo.attempts,
        duration: 0,
      };
    } else {
      throw new Error("incorrect instant boost options");
    }
  }

  @action initState(state: State) {
    if (state) {
      this.state = {
        attempts: state.attempts,
        lastUseTimestamp: null,
      };
      if (state.lastUseTimestamp) {
        this.state.lastUseTimestamp = Math.trunc(state.lastUseTimestamp * 1000);
      }
    }
    reaction(
      () => gameStore.midnight,
      () => {
        if (this.state && this.options?.attempts) {
          this.state.attempts = this.options.attempts;
        }
      }
    );
  }

  @computed public get id() {
    return this.options!.id;
  }

  @computed public get name() {
    return this.options!.name;
  }

  @computed public get desc() {
    return this.options!.description;
  }

  @computed public get dailyAttempts() {
    return this.options!.attempts;
  }

  @computed public get lastUse() {
    if (this.state?.lastUseTimestamp) {
      return this.state?.lastUseTimestamp;
    } else {
      return 0;
    }
  }

  @computed public get attempts() {
    if (this.state && this.state.attempts > 0) {
      return this.state.attempts;
    } else {
      return 0;
    }
  }

  @computed public get eta() {
    if (this.attempts) {
      const delta = gameStore.now - this.lastUse;
      let result = this.options!.duration * 1000 - delta;
      if (result < 0) result = 0;
      return result;
    } else if (this.attempts === 0) {
      return gameStore.nextMidnightTimestamp - gameStore.now;
    } else {
      return -1;
    }
  }

  @computed public get canUse() {
    return this.eta === 0;
  }

  @action.bound use() {
    if (this.canUse && this.state) {
      backend.sendBoost(this.id, 4);
      this.state.lastUseTimestamp = gameStore.now;
      this.state.attempts--;
      gameStore.refillFullEnergy();
    }
  }
}
